import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import type { EnhancedToolResult, ToolExecutionContext } from '../types';

import { REVIEW_CHECKS, type ReviewCheck, type ReviewFinding } from './review-checks';
import { Tool, type ToolConstructorArgs } from './Tool';

/**
 * The reviewer is told which checks apply to the scene it was shown, and the schema has to say the
 * same thing: a check left off that list is one `applicableChecks` already ruled out for this kind
 * of scene, so accepting it anyway blocks the agent on a defect it was never meant to be judged
 * for. Built per instance because the applicable list is per review, not per process.
 */
function buildReportFindingsSchema(checks: readonly ReviewCheck[]) {
    const checkIds = checks.map((check) => check.id) as [string, ...string[]];
    return z.object({
        findings: z
            .array(
                z.object({
                    check: z
                        .enum(checkIds)
                        .describe('The id of the defect from the list you were given.'),
                    scene_id: z
                        .string()
                        .describe('The scene the defect is in, exactly as labelled.'),
                    detail: z
                        .string()
                        .describe(
                            'What is wrong, concrete enough to act on without seeing the frames.',
                        ),
                }),
            )
            .describe('Everything you found. An empty list means there was nothing to report.'),
    });
}

export const reportFindingsSchema = buildReportFindingsSchema(REVIEW_CHECKS);
export type ReportFindingsArgs = z.infer<typeof reportFindingsSchema>;

export interface ReportFindingsToolArgs extends ToolConstructorArgs {
    /** The checks this reviewer was given — the same list its instruction lists. */
    checks: readonly ReviewCheck[];
}

/**
 * The reviewer's only way to answer. Given to the child vision conversation as its sole tool and
 * used as its stop tool, so the review comes back as validated structure rather than prose —
 * prose is what let the agent read a defect as a suggestion.
 */
export class ReportFindingsTool extends Tool<typeof reportFindingsSchema, ReportFindingsToolArgs> {
    public captured: ReviewFinding[] | undefined;

    protected override readonly schema: typeof reportFindingsSchema;
    protected override readonly toolName = TOOL_NAMES.reportFindings;
    protected override readonly toolDescription =
        'Report everything you found, or an empty list if there is nothing. This is how you answer.';

    public constructor({ checks, ...args }: ReportFindingsToolArgs) {
        super({ ...args, checks });
        this.schema = buildReportFindingsSchema(checks);
    }

    public override getToolDisplayName(): string {
        return 'Reporting findings';
    }

    public get statusIcon(): string {
        return 'rule';
    }

    public async execute(
        args: ReportFindingsArgs,
        _context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        this.captured = args.findings as ReviewFinding[];
        return { result: `Recorded ${args.findings.length} finding(s).`, isSuccess: true };
    }
}
